import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Calculator, Clock, ArrowRight, Check, Zap, Info } from 'lucide-react';
import MatrixBackground from '@/MatrixBackground';
import MarketingHeader from '@/components/MarketingHeader';
import usePageSEO from '@/hooks/usePageSEO';

const toolTimeSavings = [
  { tool: 'Contract Review', credits: 5, manualMins: 180, manualDisplay: '~3 hrs', savedPct: 90, savedDisplay: '~90%', description: 'Review and risk-flag a contract' },
  { tool: 'Due Diligence', credits: 5, manualMins: 240, manualDisplay: '4 hrs+', savedPct: 90, savedDisplay: '90%+', description: 'Analyse a due diligence document' },
  { tool: 'Legal Research', credits: 5, manualMins: 300, manualDisplay: '1 - 5 hrs', savedPct: 85, savedDisplay: '~85%', description: 'Research case law and precedents' },
  { tool: 'Document Generation', credits: 5, manualMins: 120, manualDisplay: '~2 hrs', savedPct: 90, savedDisplay: '~90%', description: 'Draft a legal document from scratch' },
  { tool: 'Deposition Prep', credits: 5, manualMins: 300, manualDisplay: '5 hrs+', savedPct: 85, savedDisplay: '85%+', description: 'Prepare deposition questions and witness analysis' },
  { tool: 'Document Comparison', credits: 5, manualMins: 180, manualDisplay: '~3 hrs', savedPct: 90, savedDisplay: '~90%', description: 'Compare two document versions clause-by-clause' },
  { tool: 'Legal Summarisation', credits: 5, manualMins: 120, manualDisplay: '1 - 3 hrs', savedPct: 90, savedDisplay: '~90%', description: 'Summarise a lengthy legal document' },
  { tool: 'Email Response', credits: 5, manualMins: 30, manualDisplay: '~30 mins', savedPct: 80, savedDisplay: '~80%', description: 'Draft a professional legal response' },
  { tool: 'Debt Recovery Letter', credits: 5, manualMins: 45, manualDisplay: '~45 mins', savedPct: 85, savedDisplay: '~85%', description: 'Generate a compliant recovery letter' },
  { tool: 'Ask Lawra', credits: 2, manualMins: 20, manualDisplay: '~20 mins', savedPct: 75, savedDisplay: '~75%', description: 'Quick legal query or research check' },
  { tool: 'More', credits: null, manualMins: null, manualDisplay: '—', savedPct: null, savedDisplay: '—', description: 'Additional AI-powered legal tools available', isPlaceholder: true },
];

const plans = [
  { id: 'trial', name: 'Free Trial', price: '0', credits: 20 },
  { id: 'basic', name: 'Basic', price: '14.99', originalPrice: '29.99', credits: 250 },
  { id: 'pro', name: 'Professional', price: '24.99', originalPrice: '49.99', credits: 600, highlight: true },
  { id: 'small_law_firm', name: 'Small Law Firm', price: '99.99', originalPrice: '199.99', credits: 2000 },
];

const avgMinsPerCredit = toolTimeSavings.filter(t => !t.isPlaceholder).reduce((sum, t) => sum + (t.manualMins * t.savedPct / 100) / t.credits, 0) / toolTimeSavings.filter(t => !t.isPlaceholder).length;

const formatHours = (mins) => {
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  if (h === 0) return `${m} mins`;
  if (m === 0) return `${h} hrs`;
  return `${h} hrs ${m} mins`;
};

const CreditCalculator = () => {
  const navigate = useNavigate();
  const [hourlyRate, setHourlyRate] = useState(250);
  const [teamSize, setTeamSize] = useState(1);

  usePageSEO({
    title: 'Credit Worth Calculator - See How Much Time & Money Lawra AI Saves',
    description: 'Calculate the estimated time and money your law firm could save with Lawra AI. Enter your hourly rate and team size to see potential ROI across all plans.',
    path: '/credit-calculator',
  });

  const planSavings = plans.map((p) => {
    const totalMins = p.credits * avgMinsPerCredit * teamSize;
    const totalHours = totalMins / 60;
    const moneyValue = totalHours * hourlyRate;
    const monthlyCost = parseFloat(p.price) * teamSize;
    const roi = monthlyCost > 0 ? Math.round(moneyValue / monthlyCost) : null;
    return { ...p, totalMins, totalHours, moneyValue, monthlyCost, roi };
  });

  return (
    <div className="landing-page">
      <MatrixBackground />
      <MarketingHeader />

      {/* Hero */}
      <section className="pricing-hero" data-testid="calculator-hero">
        <div className="pricing-hero-inner">
          <p className="uc-hero-label">Credit Worth Calculator</p>
          <h1 className="uc-hero-title">How Much Could Lawra AI Save Your Firm?</h1>
          <p className="uc-hero-subtitle">
            Enter your details below to see estimated time and cost savings across each plan. Figures are theoretical and intended as a general guide.
          </p>
        </div>
      </section>

      {/* Calculator Inputs */}
      <section className="calc-inputs-section" data-testid="calculator-inputs">
        <div className="calc-inputs-inner">
          <div className="calc-input-group">
            <label htmlFor="calc-rate">Hourly Rate (£)</label>
            <input
              id="calc-rate"
              type="number"
              min="50"
              max="2000"
              step="25"
              value={hourlyRate}
              onChange={(e) => setHourlyRate(Number(e.target.value) || 250)}
              className="roi-input calc-input-large"
              data-testid="calc-rate-input"
            />
            <span className="calc-input-hint">Your firm's average billable hourly rate</span>
          </div>
          <div className="calc-input-group">
            <label htmlFor="calc-team">Team Size</label>
            <input
              id="calc-team"
              type="number"
              min="1"
              max="100"
              step="1"
              value={teamSize}
              onChange={(e) => setTeamSize(Math.max(1, Number(e.target.value) || 1))}
              className="roi-input calc-input-large"
              data-testid="calc-team-input"
            />
            <span className="calc-input-hint">Number of people using Lawra AI</span>
          </div>
        </div>
      </section>

      {/* Per-Tool Breakdown */}
      <section className="pricing-savings" data-testid="calculator-tool-table">
        <div className="pricing-savings-inner">
          <div className="pricing-savings-header">
            <Clock size={28} className="pricing-savings-icon" />
            <h2 className="pricing-savings-title">Estimated Time Saved Per Tool Use</h2>
            <p className="pricing-savings-subtitle">
              Approximate manual time each task would take without AI assistance.
            </p>
          </div>

          <div className="savings-table-wrapper">
            <table className="savings-table" data-testid="calc-savings-table">
              <thead>
                <tr>
                  <th>Tool</th>
                  <th>What It Does</th>
                  <th>Credits</th>
                  <th>Approx. Manual Time</th>
                  <th>Est. Time Saved</th>
                  <th>Est. Value / Use</th>
                </tr>
              </thead>
              <tbody>
                {toolTimeSavings.map((t, i) => (
                  <tr key={i} className={t.isPlaceholder ? 'savings-placeholder-row' : ''}>
                    <td className="savings-tool-name">{t.tool}</td>
                    <td className="savings-desc">{t.description}</td>
                    <td className="savings-credits">{t.credits ?? '—'}</td>
                    <td className="savings-manual">{t.manualDisplay}</td>
                    <td className="savings-per-credit">{t.savedDisplay}</td>
                    <td className="savings-value">{t.isPlaceholder ? '—' : `~£${Math.round((t.manualMins * t.savedPct / 100 / 60) * hourlyRate)}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="pricing-caveat">
            * Manual time estimates are approximate and based on typical legal tasks of moderate complexity. Actual time varies significantly depending on document length, complexity, jurisdiction, and individual experience. Some complex matters may take considerably longer than shown.
          </p>
        </div>
      </section>

      {/* Plan Comparison ROI */}
      <section className="pricing-roi" data-testid="calculator-roi">
        <div className="pricing-roi-inner">
          <div className="pricing-roi-header">
            <Calculator size={28} className="pricing-savings-icon" />
            <h2 className="pricing-savings-title">Estimated Monthly Value by Plan</h2>
            <p className="pricing-savings-subtitle">
              Projected savings if all credits are used, based on your hourly rate of £{hourlyRate}{teamSize > 1 ? ` and team of ${teamSize}` : ''}.
            </p>
          </div>

          <div className="roi-cards">
            {planSavings.map((p) => (
              <div key={p.id} className={`roi-card ${p.highlight ? 'roi-card--highlight' : ''}`} data-testid={`calc-roi-${p.id}`}>
                <h4 className="roi-card-name">{p.name}</h4>
                <p className="roi-card-cost">
                  £{p.monthlyCost.toFixed(2)}/mo
                  {teamSize > 1 && <span className="roi-team-note"> ({teamSize} users)</span>}
                </p>
                <div className="roi-card-stats">
                  <div className="roi-stat">
                    <span className="roi-stat-value">~{formatHours(p.totalMins)}</span>
                    <span className="roi-stat-label">Est. time saved / month</span>
                  </div>
                  <div className="roi-stat">
                    <span className="roi-stat-value roi-stat-money">~£{Math.round(p.moneyValue).toLocaleString()}</span>
                    <span className="roi-stat-label">Est. value of time saved</span>
                  </div>
                  {p.roi && (
                    <div className="roi-stat">
                      <span className="roi-stat-value roi-stat-roi">~{p.roi}x</span>
                      <span className="roi-stat-label">Potential ROI</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <p className="pricing-caveat">
            * All figures are theoretical estimates for illustrative purposes only. They assume all monthly credits are fully utilised and are calculated using averaged time savings across all tools. Actual results depend on individual usage patterns, document complexity, matter type, user efficiency, and applicable hourly rates. These projections do not constitute a guarantee of savings or return on investment.
          </p>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="calc-disclaimer" data-testid="calculator-disclaimer">
        <div className="calc-disclaimer-inner">
          <Info size={20} />
          <div>
            <h4>Important Disclaimer</h4>
            <p>
              The Credit Worth Calculator provides rough, theoretical estimates intended as a general guide to help you evaluate Lawra AI plans. All time savings and monetary values shown are approximations based on averaged assumptions and do not account for the specific circumstances of your practice, the complexity of your matters, or individual user proficiency. Hourly rates vary widely between firms, practice areas, and jurisdictions. Actual time savings will depend on how you use the tools, the nature of your documents, and many other factors. These figures should not be relied upon as accurate projections or guarantees of any particular outcome.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section" data-testid="calculator-cta">
        <div className="cta-content">
          <h2>See the Savings for Yourself</h2>
          <p>Start with 20 free credits and experience the time savings firsthand.</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              className="try-now-btn"
              size="lg"
              data-testid="calc-cta-trial"
              onClick={async () => {
                try {
                  const { auth } = await import('../firebase');
                  if (auth.currentUser) navigate('/dashboard');
                  else navigate('/onboarding');
                } catch { navigate('/onboarding'); }
              }}
            >
              Start Free Trial
            </Button>
            <Button
              variant="ghost"
              className="use-cases-nav-link"
              size="lg"
              onClick={() => navigate('/pricing')}
              data-testid="calc-cta-pricing"
            >
              View Pricing <ArrowRight size={16} />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CreditCalculator;
